import { forwardRef } from 'react';
import type { SvgIcon, SvgIconProps } from '@mui/material';
import MuiAddIcon from '@mui/icons-material/Add';
import MuiArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import MuiArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import MuiArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MuiAscendingIcon from '@mui/icons-material/ArrowDropUp';
import MuiBarChartIcon from '@mui/icons-material/BarChart';
import MuiCancelIcon from '@mui/icons-material/Cancel';
import MuiCategoryIcon from '@mui/icons-material/Category';
import MuiCheckBoxIcon from '@mui/icons-material/CheckBox';
import MuiCheckIcon from '@mui/icons-material/Check';
import MuiChecklistIcon from '@mui/icons-material/Checklist';
import MuiCloseIcon from '@mui/icons-material/Close';
import MuiColumnsIcon from '@mui/icons-material/ViewColumnOutlined';
import MuiCompareIcon from '@mui/icons-material/CompareArrows';
import MuiCopyIcon from '@mui/icons-material/ContentCopy';
import MuiDeleteIcon from '@mui/icons-material/Delete';
import MuiDescendingIcon from '@mui/icons-material/ArrowDropDown';
import MuiDonutIcon from '@mui/icons-material/DonutLarge';
import MuiDownloadIcon from '@mui/icons-material/FileDownload';
import MuiDragIcon from '@mui/icons-material/DragIndicator';
import MuiEditIcon from '@mui/icons-material/Edit';
import MuiEventIcon from '@mui/icons-material/Event';
import MuiFilterIcon from '@mui/icons-material/FilterListRounded';
import MuiInfoIcon from '@mui/icons-material/Info';
import MuiLocationIcon from '@mui/icons-material/LocationOn';
import MuiLogoutIcon from '@mui/icons-material/Logout';
import MuiMenuIcon from '@mui/icons-material/MenuOpenRounded';
import MuiMoreIcon from '@mui/icons-material/MoreHoriz';
import MuiNotificationIcon from '@mui/icons-material/NotificationsOutlined';
import MuiOptionsIcon from '@mui/icons-material/MoreVert';
import MuiOrderIcon from '@mui/icons-material/LocalShipping';
import MuiPercentageIcon from '@mui/icons-material/Percent';
import MuiRefreshIcon from '@mui/icons-material/Refresh';
import MuiRevenueIcon from '@mui/icons-material/TrendingUp';
import MuiSearchIcon from '@mui/icons-material/Search';
import MuiSemicheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import MuiSettingsIcon from '@mui/icons-material/Settings';
import MuiStackedBarChartIcon from '@mui/icons-material/StackedBarChart';
import MuiSupportIcon from '@mui/icons-material/Support';
import MuiTableIcon from '@mui/icons-material/TableRows';
import MuiTimeIcon from '@mui/icons-material/AccessTime';
import MuiTreatmentsIcon from '@mui/icons-material/AssignmentTurnedIn';
import MuiUncheckBoxIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import MuiVideoIcon from '@mui/icons-material/VideoChat';
import {
  ArrowLeftRight,
  CalendarDays,
  ChartLine,
  ChevronDown,
  Filter,
  Home,
  Shapes,
  SlidersHorizontal,
  Telescope,
  User,
  Users,
} from 'lucide-react';
import type { BaseIconProps, IconComponent } from './types';

export type { BaseIconProps, IconComponent };

function muiIconComponent(Component: typeof SvgIcon) {
  return forwardRef<SVGSVGElement, SvgIconProps>((props, ref) => (
    <Component ref={ref} {...props} />
  ));
}

function iconComponent(Component: IconComponent) {
  return forwardRef<SVGSVGElement, BaseIconProps>(
    ({ size = '1rem', ...props }, ref) => (
      <Component ref={ref} size={size} {...props} />
    )
  );
}

// MUI-backed (SvgIconProps: className, color, sx, fontSize)
export const AddIcon = muiIconComponent(MuiAddIcon);
export const ArrowLeftIcon = muiIconComponent(MuiArrowLeftIcon);
export const ArrowRightIcon = muiIconComponent(MuiArrowRightIcon);
export const ArrowUpIcon = muiIconComponent(MuiArrowUpIcon);
export const AscendingIcon = muiIconComponent(MuiAscendingIcon);
export const BarChartIcon = muiIconComponent(MuiBarChartIcon);
export const CancelIcon = muiIconComponent(MuiCancelIcon);
export const CategoryIcon = muiIconComponent(MuiCategoryIcon);
export const CheckBoxIcon = muiIconComponent(MuiCheckBoxIcon);
export const CheckIcon = muiIconComponent(MuiCheckIcon);
export const ChecklistIcon = muiIconComponent(MuiChecklistIcon);
export const CloseIcon = muiIconComponent(MuiCloseIcon);
export const ColumnsIcon = muiIconComponent(MuiColumnsIcon);
export const CompareIcon = muiIconComponent(MuiCompareIcon);
export const CopyIcon = muiIconComponent(MuiCopyIcon);
export const DeleteIcon = muiIconComponent(MuiDeleteIcon);
export const DescendingIcon = muiIconComponent(MuiDescendingIcon);
export const DonutIcon = muiIconComponent(MuiDonutIcon);
export const DownloadIcon = muiIconComponent(MuiDownloadIcon);
export const DragIcon = muiIconComponent(MuiDragIcon);
export const EditIcon = muiIconComponent(MuiEditIcon);
export const EventIcon = muiIconComponent(MuiEventIcon);
export const FilterIcon = muiIconComponent(MuiFilterIcon);
export const InfoIcon = muiIconComponent(MuiInfoIcon);
export const LocationIcon = muiIconComponent(MuiLocationIcon);
export const LogoutIcon = muiIconComponent(MuiLogoutIcon);
export const MenuIcon = muiIconComponent(MuiMenuIcon);
export const MoreIcon = muiIconComponent(MuiMoreIcon);
export const NotificationIcon = muiIconComponent(MuiNotificationIcon);
export const OptionsIcon = muiIconComponent(MuiOptionsIcon);
export const OrderIcon = muiIconComponent(MuiOrderIcon);
export const PercentageIcon = muiIconComponent(MuiPercentageIcon);
export const RefreshIcon = muiIconComponent(MuiRefreshIcon);
export const RevenueIcon = muiIconComponent(MuiRevenueIcon);
export const SearchIcon = muiIconComponent(MuiSearchIcon);
export const SemicheckBoxIcon = muiIconComponent(MuiSemicheckBoxIcon);
export const SettingsIcon = muiIconComponent(MuiSettingsIcon);
export const StackedBarChartIcon = muiIconComponent(MuiStackedBarChartIcon);
export const SupportIcon = muiIconComponent(MuiSupportIcon);
export const TableIcon = muiIconComponent(MuiTableIcon);
export const TimeIcon = muiIconComponent(MuiTimeIcon);
export const TreatmentsIcon = muiIconComponent(MuiTreatmentsIcon);
export const UncheckBoxIcon = muiIconComponent(MuiUncheckBoxIcon);
export const VideoIcon = muiIconComponent(MuiVideoIcon);

// lucide-backed (LucideProps: className, color, size, strokeWidth)
export const ArrowDownIcon = iconComponent(ChevronDown);
export const CalendarIcon = iconComponent(CalendarDays);
export const FunnelIcon = iconComponent(Filter);
export const HomeIcon = iconComponent(Home);
export const PeopleIcon = iconComponent(Users);
export const PersonIcon = iconComponent(User);
export const PropertiesIcon = iconComponent(Shapes);
export const ReportsIcon = iconComponent(ChartLine);
export const SegmentsIcon = iconComponent(SlidersHorizontal);
export const SnapshotsIcon = iconComponent(Telescope);
export const SwapIcon = iconComponent(ArrowLeftRight);
